import React, { useEffect, useMemo, useState } from "react"
import { useOutletContext } from "react-router-dom"

import {
  TYPE_NONE,
  TYPE_HIDDEN,
  TYPE_STRING,
  TYPE_REDACTOR,
} from "@components/Form"
import { W0, W1 } from "@components/Constants"
import { TemplateVariablesDropdown } from "@components/Templates"

const LABEL_SUBJECT = "Subject"
const LABEL_MESSAGE = "Message"
const LABEL_SUBJECT_PLACEHOLDER = "Notification Subject"


const useCustomTemplateInputs = (form, templateKey, shouldRender = false) => {
  const { projectTemplatesStore } = useOutletContext()
  const { getTemplateByKey } = projectTemplatesStore

  const template = getTemplateByKey(templateKey) || {}

  const { html, source, subject, variables } = template

  const isTemplateReady =
    shouldRender &&
    source != null &&
    html != null &&
    Array.isArray(variables)

  const [ isPrefilled, setIsPrefilled ] = useState(false)

  useEffect(() => {
    // Reset whenever the template selection or visibility changes.
    setIsPrefilled(false)
  }, [ templateKey, shouldRender ])

  useEffect(() => {
    if (!isTemplateReady) {
      return
    }

    if (isPrefilled) {
      return
    }

    // Avoid relying on antd `initialValue` timing (production race).
    form.setFieldValue("customSubjectTemplate", subject)
    form.setFieldValue("customSourceTemplate", source)
    form.setFieldValue("customHtmlTemplate", html)

    const timeoutId = window.setTimeout(() => {
      setIsPrefilled(true)
    }, 250)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [
    form,
    isTemplateReady,
    isPrefilled,
    subject,
    source,
    html
  ])

  return useMemo(() => {
    if (!isTemplateReady || !isPrefilled) {
      return [
        {
          name: "customSubjectTemplate",
          type: TYPE_NONE,
        },
        {
          name: "customSourceTemplate",
          type: TYPE_NONE,
        },
        {
          name: "customHtmlTemplate",
          type: TYPE_NONE,
        },
      ]
    }

    const tags = variables
      .map(variable => variable.split(":")[1])

    const appendVariable = variable => {
      const currentSubject = form.getFieldValue('customSubjectTemplate')
      const value = `${currentSubject} ${variable}`

      form.setFieldValue('customSubjectTemplate', value)
    }

    return [{
      name: "customSubjectTemplate",
      type: TYPE_STRING,
      label: LABEL_SUBJECT,
      placeholder: LABEL_SUBJECT_PLACEHOLDER,
      initialValue: subject,
      itemProps: {
        style: {
          maxWidth: 492,
          marginBottom: W1,
        }
      },
      inputProps: {
        size: "large",
        suffix: (
          <TemplateVariablesDropdown
            variables={variables}
            appendVariable={appendVariable}
          />
        )
      }
    },
    {
      name: "customSourceTemplate",
      type: TYPE_REDACTOR,
      label: LABEL_MESSAGE,
      initialValue: source,
      itemProps: {
        style: {
          maxWidth: 492,
          marginBottom: W0,
        }
      },
      inputProps: {
        emailTargetPath: 'customHtmlTemplate',
        options: {
          plugins: [
            'mergetag'
          ],
          mergetag: {
            items: tags
          }
        }
      }
    },
    {
      name: "customHtmlTemplate",
      initialValue: html,
      type: TYPE_HIDDEN,
    }]
  }, [
    form,
    template,
    shouldRender,
    isTemplateReady,
    isPrefilled
  ])
}

export default useCustomTemplateInputs
